import io from 'socket.io-client'
import Config from '@/config'
import EventBus from '@/eventBus'
import QuizStatuses from '@/constants/QuizStatuses'
import sentry from '@/3rd-parties/sentry'
import MainWebSocketSubscriber from '@/websocket/subscribers/main.websocket.subscriber'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import { logger } from '@ahaslides-product/frontend-toolkit'
import store from './store'

const logFn = logger.extend('socket')

let socket = null

const _handleSocketQuizTimestamp = (doc) => {
  if (
    !store.state.presentation.audiencePacing &&
    doc.slideActive == store.getters.activeActivity.id
  ) {
    if ((doc.quizStatus > store.getters.activeActivity.quizStatus
      && doc.quizTimestamp[0] != store.getters.activeActivity.quizTimestamp[0]
    ) ||
      (store.getters.activeActivity.quizStatus === QuizStatuses.QUESTION_SCREEN &&
        doc.quizTimestamp[0] < store.getters.activeActivity.quizTimestamp[0]
      )) {
      store.dispatch('updateSlide', {
        quizStatus: doc.quizStatus,
        quizTimestamp: doc.quizTimestamp,
      })

      setTimeout(() => {
        EventBus.$emit('recheck-progress-bar')
      }, 300)
    }
  }
}

const _handleSocketQuizHint = (doc) => {
  if (
    !store.state.presentation.audiencePacing &&
    doc.slideActive == store.getters.activeActivity.id &&
    doc.numberOfHintsShown !== store.getters.activeActivity.numberOfHintsShown
  ) {
    store.dispatch('updateSlide', {
      numberOfHintsShown: doc.numberOfHintsShown,
      hintsShowingIndex: doc.hintsShowingIndex,
    })
    setTimeout(() => {
      EventBus.$emit('hints: update-show-hints', doc)
    }, 200)
  }
}

const _handleConfirmNextSlide = (doc) => {
  if (store.getters.activeActivity && doc.slideActive != store.getters.activeActivity.id) {
    store.state.currentDataNextSlide = null
    EventBus.$emit('confirmNextSlide', doc.slideActive)
  }
}

export default {
  init: (cb, accessCode, audienceId) => {
    const query = {
      type: 'audience',
      accessCode,
      audience: audienceId,
    }

    if (store.state.audienceName) {
      query.audienceName = store.state.audienceName
    }

    try {
      const { planType, isAudienceAuthentication } = store.state.presentation
      if (isAudienceAuthentication && store.state.authenticationCode && (planType === 'pro-yearly' || planType === 'pro-monthly' || planType === 'one-time-large' || planType === 'white-label')) {
        query.authCode = `${accessCode}-${store.state.authenticationCode}`
      }
    } catch (error) {
      sentry.captureException(error, { extra: { message: 'Error when check authenticationCode' } })
    }
    socket = io(Config.baseUrlSocket, {
      path: '/api/socket.io-client',
      query,
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: {
        audienceId,
      },
    })
    logFn('init', { query, audienceId })

    socket.on('connect', () => {
      // TODO: migrate all of these legacy websocket subscribers to new websocket subscribers
      cb(socket.id)
      socket.on(socket.id, (doc) => {
        if (!store.getters['featureToggles/canUseNewChangeSlideMechanism']) {
          _handleSocketQuizTimestamp(doc)
        }

        _handleSocketQuizHint(doc)
        if (doc.quizStatus === QuizStatuses.RESULT_SCREEN) {
          EventBus.$emit('quizGetResult', doc.slideActive)
        }

        if (!store.getters['featureToggles/canSelfPaceUseNewChangeSlideMechanism']) {
          /**
           * ignore slideActive socket.io msg since the sync back sometimes can be slower than new mechanism
           * hence force audience to move to a wrong slide
           */
          _handleConfirmNextSlide(doc)
        }

        // remove class when update query or reconnect
        document.body.classList.remove('hide-popup-connect-lost')
      })

      if (socket.io.opts.query.authCode) {
        EventBus.$emit('audience-authen-connected', {
          authCode: socket.io.opts.query.authCode,
        })
      }

      store.state.socketDisconnect = false

      SystemConnection.detectSocketConnection(CONNECTION_STATUS.GOOD)
      logFn('connect', { id: socket.id })
    })

    MainWebSocketSubscriber.init(socket)

    socket.on('server_timestamp', (payload) => {
      store.commit('setOffsetTime', Date.now() - payload)
    })

    socket.on('connect_error', (err) => {
      // comment out due to error
      // [vuex] unknown mutation type: setWebsocketConnectionError
      // store.commit('setWebsocketConnectionError', err.message)

      SystemConnection.detectSocketConnection(CONNECTION_STATUS.ERROR)
      logFn('connect_error', err)
    })

    socket.on('disconnect', (reason, details) => {
      if (socket.active) {
        // temporary disconnection, the socket will automatically try to reconnect
        logFn('disconnect but still active', { reason, details })
      } else {
        // the connection was forcefully closed by the server or the client itself
        // in that case, `socket.connect()` must be manually called in order to reconnect
        logFn('disconnect', { reason, details })
      }
    })

    setInterval(() => {
      socket.emit('server_timestamp')
    }, 60000)
    socket.emit('server_timestamp')
  },

  updateQuery: (params) => {
    logFn('updateQuery', params)
    socket.disconnect()
    socket.io.opts.query = { ...socket.io.opts.query, ...params }
    socket.connect()
  },

  subscribe: (channel, callback) => {
    socket.on(channel, (doc) => {
      return callback(doc)
    })
  },

  unSubscribe: (channel) => {
    if (!socket) {
      return
    }
    socket.removeAllListeners(channel)
  },

  getSocketId: () => {
    return socket.id
  },

  close: () => {
    if (!socket) return
    socket.close()
  },

  getSocket: () => {
    return socket
  },
  on(...props) {
    return socket.on(...props)
  },
  off(...props) {
    return socket.off(...props)
  },
  emit(...props) {
    return socket.emit(...props)
  },
  handleSocketQuizTimestamp: _handleSocketQuizTimestamp,
  handleConfirmNextSlide: _handleConfirmNextSlide,
}
